import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Row, Space, Switch, Table, Tag, message } from "antd";
import { PageHeader } from "components/pageHeader";
import { getColumnSearchProps } from "common/antd-utils";
import {
  useFetchDebtorAssetCategoriesQuery,
  useUpdateDebtorAssetCategoryMutation,
} from "features/debtorAssetCategories/debtorAssetCategoriesAPI";
import { useFetchAllDebtorAssetCustomFieldsQuery } from "features/userDefinedFields/debtorAssetCustomFieldsAPI";
import DebtorAssetCategoryModal from "features/debtorAssetCategories/components/debtorAssetCategoryModal";
import { PERMISSIONS } from "features/permissions/constants";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useMemo, useRef, useState } from "react";
import styled from "styled-components";

const StyledHeader = styled(PageHeader)`
  margin-bottom: 0;
`;

const StyledRow = styled(Row)`
  margin-bottom: 8px;
`;

const StyledTagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export default function DebtorAssetCategoriesTable() {
  const {
    data: {
      [PERMISSIONS.DEBTOR_ASSET_CATEGORY__GET]: isGetAuthorized,
      [PERMISSIONS.DEBTOR_ASSET_CATEGORY__CREATE]: isCreateAuthorized,
      [PERMISSIONS.DEBTOR_ASSET_CATEGORY__UPDATE]: isUpdateAuthorized,
    },
  } = useAuthorizations();

  const [isModalVisible, setModalVisible] = useState(false);
  const [activeRecord, setActiveRecord] = useState(null);

  const { data: categories = [], isLoading } = useFetchDebtorAssetCategoriesQuery(undefined, {
    skip: !isGetAuthorized,
  });
  const { data: customFields = [] } = useFetchAllDebtorAssetCustomFieldsQuery();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateDebtorAssetCategoryMutation();

  // Build custom field ID to name map
  const customFieldIdToNameMap = useMemo(() => {
    return customFields.reduce((acc, field) => {
      acc[field.id] = field.name;
      return acc;
    }, {});
  }, [customFields]);

  const onAddNew = () => {
    setActiveRecord(null);
    setModalVisible(true);
  };

  const onCloseModal = () => {
    setActiveRecord(null);
    setModalVisible(false);
  };

  const handleArchiveToggle = async (record, checked) => {
    const result = await updateCategory({
      id: record.id,
      isArchived: !checked,
    });
    if ("data" in result) {
      message.success(`Category ${checked ? "activated" : "archived"} successfully`);
    }
  };

  const [filteredInfo, setFilteredInfo] = useState({});
  const searchInput = useRef(null);

  const handleSearch = (selectedKeys, confirm, _dataIndex) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleChange = (_, filters) => {
    setFilteredInfo(filters);
  };

  const columns = [
    {
      title: "Active",
      dataIndex: "isArchived",
      key: "isArchived",
      width: 80,
      render: (isArchived, record) => (
        <Switch
          checked={!isArchived}
          onChange={(checked) => handleArchiveToggle(record, checked)}
          loading={isUpdating}
          disabled={!isUpdateAuthorized}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => text ?? "-",
      ...getColumnSearchProps("name", searchInput, handleSearch, handleReset),
      filteredValue: filteredInfo?.name ?? null,
      sorter: (a, b) => a.name.localeCompare(b.name),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Description",
      dataIndex: "notes",
      key: "notes",
      render: (text) => text || "-",
      ...getColumnSearchProps("notes", searchInput, handleSearch, handleReset),
      filteredValue: filteredInfo?.notes ?? null,
    },
    {
      title: "Custom Fields",
      dataIndex: "debtorAssetCustomFieldIds",
      key: "debtorAssetCustomFieldIds",
      render: (fieldIds) => {
        if (!fieldIds || fieldIds.length === 0) {
          return "-";
        }
        return (
          <StyledTagContainer>
            {fieldIds.map((fieldId) => (
              <Tag key={fieldId}>{customFieldIdToNameMap[fieldId] || fieldId}</Tag>
            ))}
          </StyledTagContainer>
        );
      },
    },
    {
      title: "Actions",
      fixed: "right",
      key: "action",
      width: 80,
      hidden: !isUpdateAuthorized,
      render: (_, record) => (
        <Space size="middle">
          {isUpdateAuthorized && (
            <EditOutlined
              key="edit"
              onClick={() => {
                setActiveRecord(record);
                setModalVisible(true);
              }}
            />
          )}
        </Space>
      ),
    },
  ].filter((column) => !column.hidden);

  return (
    <>
      <StyledHeader>Debtor Asset Categories</StyledHeader>
      <StyledRow align="middle">
        <span>Manage debtor asset categories.</span>
        {isCreateAuthorized && (
          <Button onClick={onAddNew} icon={<PlusOutlined />} type="link">
            Add Category
          </Button>
        )}
      </StyledRow>
      <Table
        rowKey="id"
        loading={isLoading || isUpdating}
        bordered
        scroll={{ x: "max-content" }}
        columns={columns}
        dataSource={categories}
        onChange={handleChange}
      />
      {isModalVisible && (
        <DebtorAssetCategoryModal
          record={activeRecord}
          customFields={customFields}
          open={isModalVisible}
          onOk={onCloseModal}
          onCancel={onCloseModal}
        />
      )}
    </>
  );
}
